import { AutoAwesome } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { StructureSet } from '~/domain/patient/structureset';
import { useAvailableStructureSetsView } from '~/hooks/domain/useAvailableStructureSetsView';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useSegmentationMetrics } from '~/hooks/workflows/backend/tasks/useSegmentationMetrics';
import { handleError } from '~/utils/errors';
import { cutUID } from '~/utils/string';
import SegmetricsTable from './SegmetricsTable';

export default function Segmetrics() {
    const [testStructureSet, setTestStructureSet] = useState<StructureSet>();
    const [referenceStructureSet, setReferenceStructureSet] =
        useState<StructureSet>();
    const availableStructureSets = useAvailableStructureSetsView(true);
    const patient = useSelectedPatient();
    const { submit, loading, data } = useSegmentationMetrics();

    // The Segmetrics service needs a live StructureSet to read DICOM
    // blobs from. Resolve the selected view back to its live SS via
    // the patient aggregate.
    const resolveLive = (uid: string): StructureSet | undefined => {
        return patient?.structureSets.find((ss) => ss.dicomUid === uid);
    };

    const handleSelectTestStructureSet = (event: SelectChangeEvent) => {
        const selectedView = availableStructureSets.find(
            (structureSet) => structureSet.sopInstanceUid === event.target.value
        );
        const selectedLive = selectedView
            ? resolveLive(selectedView.sopInstanceUid)
            : undefined;
        const wasReference =
            selectedLive !== undefined &&
            selectedLive.dicomUid === referenceStructureSet?.dicomUid;
        setTestStructureSet(selectedLive);
        if (wasReference) {
            setReferenceStructureSet(testStructureSet);
        }
    };

    const handleSelectReferenceStructure = (event: SelectChangeEvent) => {
        const selectedView = availableStructureSets.find(
            (structureSet) => structureSet.sopInstanceUid === event.target.value
        );
        const selectedLive = selectedView
            ? resolveLive(selectedView.sopInstanceUid)
            : undefined;
        const wasTest =
            selectedLive !== undefined &&
            selectedLive.dicomUid === testStructureSet?.dicomUid;
        setReferenceStructureSet(selectedLive);
        if (wasTest) {
            setTestStructureSet(referenceStructureSet);
        }
    };

    /**
     * Comparison reads domain contours, so every registry-held grid
     * Environment is flushed first. A failed flush or a
     * set whose edits were lost sends nothing: metrics of contours the user
     * does not see would read as a verification of the ones they do.
     */
    const startComparison = (test: StructureSet, reference: StructureSet) => {
        try {
            if (
                !ensureStructureSetsCurrent(
                    getDatasetEnvironmentRegistry(),
                    [test, reference],
                    'Comparison'
                )
            ) {
                return;
            }
        } catch (error) {
            handleError(
                error,
                'Failed to save contour edits before comparison.'
            );
            return;
        }
        submit(test, reference);
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    <FormattedMessage id="runStructureComparison" />
                </Typography>
                <Typography variant="body2">
                    <FormattedMessage id="runStructureComparisonDescription" />
                </Typography>
            </Box>
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    gap: 0.75,
                    px: 1.5,
                    py: 1,
                    mb: 1,
                    color: 'warning.main',
                    border: '1px solid',
                    borderColor: 'warning.main',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    borderRadius: `${theme.shape.borderRadius}px`,
                })}
            >
                <TriangleAlert
                    size={16}
                    style={{ flexShrink: 0, marginTop: 2 }}
                />
                <Typography variant="caption" color="inherit">
                    Structure names must be identical between the test and
                    reference sets to get comparison results. You can rename
                    structures in the workspace if needed.
                </Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={2} mt={0.5}>
                <FormControl fullWidth>
                    <InputLabel
                        id="test-structure-set-select-label"
                        size="small"
                        disabled={loading}
                    >
                        Test Structure Set
                    </InputLabel>
                    <Select
                        labelId="test-structure-set-select-label"
                        id="test-structure"
                        value={testStructureSet?.dicomUid ?? ''}
                        label={'Test Structure Set'}
                        onChange={handleSelectTestStructureSet}
                        size="small"
                        disabled={loading}
                    >
                        {availableStructureSets.map((structureSet) => (
                            <MenuItem
                                key={structureSet.sopInstanceUid}
                                value={structureSet.sopInstanceUid}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                >
                                    <Typography fontSize={16}>
                                        {structureSet.displayName}
                                    </Typography>
                                    <Typography fontSize={14} variant="body2">
                                        <SensitiveInfoTypography>
                                            {cutUID(
                                                structureSet.sopInstanceUid
                                            )}
                                        </SensitiveInfoTypography>
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel
                        id="reference-structure-set-select-label"
                        size="small"
                        disabled={loading}
                    >
                        Reference Structure Set
                    </InputLabel>
                    <Select
                        labelId="reference-structure-set-select-label"
                        id="reference-structure"
                        value={referenceStructureSet?.dicomUid ?? ''}
                        label={'Reference Structure Set'}
                        onChange={handleSelectReferenceStructure}
                        size="small"
                        disabled={loading}
                    >
                        {availableStructureSets.map((structureSet) => (
                            <MenuItem
                                key={structureSet.sopInstanceUid}
                                value={structureSet.sopInstanceUid}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                >
                                    <Typography fontSize={16}>
                                        {structureSet.displayName}
                                    </Typography>
                                    <Typography fontSize={14} variant="body2">
                                        <SensitiveInfoTypography>
                                            {cutUID(
                                                structureSet.sopInstanceUid
                                            )}
                                        </SensitiveInfoTypography>
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {data && <SegmetricsTable data={data} />}

            <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
            >
                <Stack gap={0.5}>
                    <PseudonymizeCheckbox disabled={loading} />
                    <Button
                        onClick={() => {
                            if (testStructureSet && referenceStructureSet) {
                                startComparison(
                                    testStructureSet,
                                    referenceStructureSet
                                );
                            }
                        }}
                        disabled={
                            loading ||
                            !testStructureSet ||
                            !referenceStructureSet
                        }
                        variant="outlined"
                        endIcon={<AutoAwesome />}
                        sx={{ width: 160 }}
                        startIcon={
                            loading && (
                                <CircularProgress size={12} color="inherit" />
                            )
                        }
                    >
                        {loading ? (
                            <FormattedMessage id="loading" />
                        ) : (
                            <FormattedMessage id="start" />
                        )}
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
